import { useEffect, useState } from "react";
import { Mail, ExternalLink, Loader2 } from "lucide-react";
import Modal from "@/components/Modal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buildContainerEmailDraft, gmailComposeUrl } from "@/lib/outreach";
import { toast } from "sonner";

/**
 * ContainerEmailModal — email the customer the shipment's invoice number, BL
 * number and ETA. Like the rest of the outreach in this app it is entirely
 * client-side: the draft opens in the rep's own Gmail compose window, so
 * nothing is sent from the server and no provider credentials are involved.
 */
export default function ContainerEmailModal({ container, onClose }) {
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  // The container row only carries the customer's name; the emails live on the
  // customer record. A sales user whose access hides this customer gets a 403 —
  // the draft still opens, they just type the recipient in themselves.
  useEffect(() => {
    let alive = true;
    (async () => {
      const c = container?.customer_id
        ? await api.get(`/customers/${container.customer_id}`).then((r) => r.data).catch(() => null)
        : null;
      if (!alive) return;
      const draft = buildContainerEmailDraft(container, c, user);
      setCustomer(c);
      setTo(draft.to);
      setSubject(draft.subject);
      setBody(draft.body);
      setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container?.id]);

  const emailOptions = customer?.emails || [];
  // Gmail takes several recipients in one compose window, comma-separated.
  const selectedEmails = to.split(",").map((s) => s.trim()).filter(Boolean);
  const toggleEmail = (v) => {
    const next = selectedEmails.includes(v)
      ? selectedEmails.filter((e) => e !== v)
      : [...selectedEmails, v];
    setTo(next.join(", "));
  };

  const open = () => {
    if (!to.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    window.open(gmailComposeUrl({ to: to.trim(), subject, body }), "_blank", "noopener,noreferrer");
    toast.success("Opening Gmail…");
    onClose();
  };

  const sub = [container?.customer_name || customer?.company, container?.container_number || container?.bl_number]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal title="Email Customer" subtitle={sub || "Shipment update"} onClose={onClose} size="lg">
      {loading ? (
        <div className="flex items-center gap-2" style={{ padding: 24, color: "var(--text-2)", fontSize: 13 }}>
          <Loader2 size={14} className="spin" /> Preparing draft…
        </div>
      ) : (
        <div className="grid gap-4" data-testid="container-email-modal">
          <div style={{ fontSize: 12, color: "var(--text-2)" }}>
            Draft carries the invoice number, BL number and ETA for this shipment. Edit anything below,
            then open it in Gmail.
          </div>

          <div>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>To (email)</label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="input-base"
              placeholder="name@company.com, other@company.com"
              data-testid="container-email-to"
            />
            {emailOptions.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-2 items-center">
                <span style={{ fontSize: 10, color: "var(--text-2)", marginRight: 2 }}>Tap to add / remove recipients:</span>
                {emailOptions.map((v) => {
                  const active = selectedEmails.includes(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => toggleEmail(v)}
                      className="tag"
                      style={{
                        fontSize: 10,
                        cursor: "pointer",
                        background: active ? "var(--primary)" : "var(--surface-2)",
                        borderColor: active ? "var(--primary)" : "var(--border)",
                        color: active ? "#fff" : "var(--text-2)",
                      }}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            )}
            {!to.trim() && (
              <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 4 }}>
                {container?.customer_id
                  ? "No email on file for this customer — type one in."
                  : "This container isn’t linked to a customer — type the recipient in."}
              </div>
            )}
          </div>

          <div>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-base" data-testid="container-email-subject" />
          </div>

          <div>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="input-base"
              style={{ resize: "vertical", fontFamily: "inherit", whiteSpace: "pre-wrap" }}
              data-testid="container-email-body"
            />
          </div>

          <div className="flex gap-3 justify-end mt-2">
            <button onClick={onClose} className="btn-outline" data-testid="container-email-cancel">Cancel</button>
            <button onClick={open} className="btn-primary" style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="container-email-open">
              <Mail size={14} /> Open in Gmail <ExternalLink size={13} />
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
